/**
 * Vincent B796 backlog (Eric 2026-02-12) — Section admin qui affiche
 * les endpoints qui déclenchent le plus de 429 (rate-limit dépassé).
 *
 * Utilise `GET /api/admin/rate-limit-diagnostic` (rolling window 15 min,
 * en mémoire côté backend). Actualisable à la demande + bouton reset
 * pour repartir à zéro après un fix.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, Gauge, Trash, WarningOctagon } from "@phosphor-icons/react";
import { API_BASE as API } from "../../lib/apiBase";

const METHOD_COLORS = {
  GET: "bg-emerald-100 text-emerald-800 border-emerald-300",
  POST: "bg-blue-100 text-blue-800 border-blue-300",
  PUT: "bg-amber-100 text-amber-800 border-amber-300",
  PATCH: "bg-violet-100 text-violet-800 border-violet-300",
  DELETE: "bg-red-100 text-red-800 border-red-300",
};

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export default function RateLimitDiagnosticSection({ lang = "fr" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(
        `${API}/api/admin/rate-limit-diagnostic`,
        { withCredentials: true }
      );
      setData(resp);
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (lang === "fr" ? "Échec du chargement" : "Load failed")
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  const doReset = async () => {
    setResetting(true);
    try {
      const { data: resp } = await axios.post(
        `${API}/api/admin/rate-limit-diagnostic/reset`,
        {},
        { withCredentials: true }
      );
      toast.success(
        lang === "fr"
          ? `Compteur remis à zéro (${resp.purged} événement${resp.purged !== 1 ? "s" : ""} purgé${resp.purged !== 1 ? "s" : ""})`
          : `Counter reset (${resp.purged} event${resp.purged !== 1 ? "s" : ""} purged)`
      );
      await load();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (lang === "fr" ? "Échec du reset" : "Reset failed")
      );
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const total = data?.total_hits || 0;
  const isHot = total > 20; // Seuil visuel : > 20 hits/15min = suspect.

  return (
    <div
      className="border border-zinc-200 rounded-lg bg-white overflow-hidden mb-6"
      data-testid="rate-limit-diagnostic-section"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-gradient-to-r from-amber-50 to-white">
        <div className="flex items-center gap-2">
          <Gauge size={20} weight="bold" className={isHot ? "text-red-600" : "text-amber-600"} />
          <div>
            <h2 className="text-sm font-bold text-zinc-800">
              {lang === "fr" ? "Diagnostic Rate Limit (HTTP 429)" : "Rate Limit Diagnostic (HTTP 429)"}
            </h2>
            <p className="text-[11px] text-zinc-500">
              {lang === "fr"
                ? `Fenêtre glissante ${data?.window_minutes ?? 15} min. Identifie les endpoints/pollers qui saturent la limite 200 req/min/IP.`
                : `Rolling ${data?.window_minutes ?? 15} min window. Spot endpoints/pollers hitting the 200 req/min/IP cap.`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-xs font-medium disabled:opacity-50"
            data-testid="rate-limit-refresh-btn"
            title={lang === "fr" ? "Recharger" : "Refresh"}
            type="button"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
            {lang === "fr" ? "Recharger" : "Refresh"}
          </button>
          <button
            onClick={doReset}
            disabled={resetting || total === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-300 bg-white hover:bg-red-50 text-red-700 text-xs font-medium disabled:opacity-40"
            data-testid="rate-limit-reset-btn"
            title={lang === "fr" ? "Remettre le compteur à zéro" : "Reset counter"}
            type="button"
          >
            <Trash size={12} />
            {lang === "fr" ? "Reset" : "Reset"}
          </button>
        </div>
      </div>

      <div className="p-4">
        {loading && !data ? (
          <p className="text-sm text-zinc-500">
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </p>
        ) : total === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm font-medium text-emerald-700">
              {lang === "fr"
                ? "Aucun rejet 429 dans les 15 dernières minutes."
                : "No 429 rejections in the last 15 minutes."}
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">
              {lang === "fr"
                ? "Si des utilisateurs ont vu le toast « Trop de requêtes », attends leur prochain refresh."
                : "If users saw the \"Too many requests\" toast, wait for their next refresh."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Bandeau total */}
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded border ${
                isHot
                  ? "bg-red-50 border-red-300 text-red-800"
                  : "bg-amber-50 border-amber-300 text-amber-800"
              }`}
              data-testid="rate-limit-total-banner"
            >
              <WarningOctagon size={16} weight="fill" />
              <span className="text-sm font-semibold">
                {total} {lang === "fr" ? "rejet" : "rejection"}
                {total !== 1 ? "s" : ""} 429
              </span>
              <span className="text-xs text-zinc-600">
                {lang === "fr" ? "dans la fenêtre" : "in the window"} · {lang === "fr" ? "généré" : "generated"} {fmtTime(data.generated_at)}
              </span>
            </div>

            {/* Top endpoints */}
            <div>
              <h3 className="text-xs font-bold text-zinc-700 uppercase tracking-wider mb-2">
                {lang === "fr" ? "Endpoints les plus touchés" : "Most impacted endpoints"}
              </h3>
              <div className="overflow-x-auto border border-zinc-200 rounded">
                <table className="w-full text-xs" data-testid="rate-limit-top-endpoints-table">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="text-left px-2 py-1 font-semibold text-zinc-600">{lang === "fr" ? "Méthode" : "Method"}</th>
                      <th className="text-left px-2 py-1 font-semibold text-zinc-600">{lang === "fr" ? "Chemin" : "Path"}</th>
                      <th className="text-right px-2 py-1 font-semibold text-zinc-600">{lang === "fr" ? "Hits" : "Hits"}</th>
                      <th className="text-right px-2 py-1 font-semibold text-zinc-600">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_endpoints.map((e, i) => (
                      <tr
                        key={`${e.method}-${e.path}-${i}`}
                        className={i % 2 === 0 ? "bg-white" : "bg-zinc-50"}
                        data-testid={`rate-limit-endpoint-row-${i}`}
                      >
                        <td className="px-2 py-1">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${METHOD_COLORS[e.method] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>
                            {e.method}
                          </span>
                        </td>
                        <td className="px-2 py-1 font-mono text-[11px] truncate max-w-md" title={e.path}>{e.path}</td>
                        <td className="px-2 py-1 text-right font-semibold">{e.count}</td>
                        <td className="px-2 py-1 text-right text-zinc-500">{e.share_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top users + IPs côte à côte */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.top_users?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-zinc-700 uppercase tracking-wider mb-2">
                    {lang === "fr" ? "Utilisateurs les plus touchés" : "Most impacted users"}
                  </h3>
                  <div className="border border-zinc-200 rounded overflow-hidden" data-testid="rate-limit-top-users">
                    {data.top_users.map((u, i) => (
                      <div
                        key={u.user_id}
                        className={`flex justify-between items-center px-2 py-1 text-xs ${i % 2 === 0 ? "bg-white" : "bg-zinc-50"}`}
                      >
                        <span className="font-mono truncate">{u.user_id}</span>
                        <span className="font-semibold text-zinc-700">{u.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.top_ips?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-zinc-700 uppercase tracking-wider mb-2">
                    {lang === "fr" ? "IPs les plus touchées" : "Most impacted IPs"}
                  </h3>
                  <div className="border border-zinc-200 rounded overflow-hidden" data-testid="rate-limit-top-ips">
                    {data.top_ips.map((ip, i) => (
                      <div
                        key={ip.ip}
                        className={`flex justify-between items-center px-2 py-1 text-xs ${i % 2 === 0 ? "bg-white" : "bg-zinc-50"}`}
                      >
                        <span className="font-mono truncate">{ip.ip}</span>
                        <span className="font-semibold text-zinc-700">{ip.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recent events (audit trail) */}
            {data.recent_events?.length > 0 && (
              <details className="border border-zinc-200 rounded">
                <summary className="px-2 py-1.5 bg-zinc-50 text-xs font-semibold text-zinc-700 cursor-pointer">
                  {lang === "fr" ? `${data.recent_events.length} événements récents` : `${data.recent_events.length} recent events`}
                </summary>
                <div className="max-h-64 overflow-y-auto" data-testid="rate-limit-recent-events">
                  {data.recent_events.map((e, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 px-2 py-1 text-[11px] font-mono border-t border-zinc-100 ${i % 2 === 0 ? "bg-white" : "bg-zinc-50"}`}
                    >
                      <span className="text-zinc-400 w-16">{fmtTime(e.ts)}</span>
                      <span className={`inline-block px-1 rounded text-[9px] font-bold border ${METHOD_COLORS[e.method] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>
                        {e.method}
                      </span>
                      <span className="flex-1 truncate">{e.path}</span>
                      <span className="text-zinc-500 truncate w-32">{e.ip}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
